import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import IntlWrapperContext from '@aurora/shared-client/components/context/IntlContext/IntlWrapperContext';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import Icons from '@aurora/shared-client/icons';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import dynamic from 'next/dynamic';
import React, { useContext, useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import useTranslation from '../../useTranslation';
import localStyles from './TermsOfServiceActionMenu.module.pcss';
import type { TermsOfServiceType } from '../TermsOfServiceHelper';

const TermsOfServiceEditModal = dynamic(
  () => import('../TermsOfServiceEditModal/TermsOfServiceEditModal'),
  {
    ssr: false
  }
);
const LanguageSelectionModal = dynamic(
  () => import('./LanguageSelectionModal/LanguageSelectionModal'),
  {
    ssr: false
  }
);

interface Props extends React.PropsWithChildren {
  /**
   * Type of terms of service
   */
  termsOfServiceType: TermsOfServiceType;
}

/**
 * Display action menu for terms of service
 *
 * @author Amit Agrawal
 */
const TermsOfServiceActionMenu: React.FC<Props> = ({ termsOfServiceType }) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.TERMS_OF_SERVICE_ACTION_MENU
  );
  const {
    community: {
      userContext: { canUpdateNode }
    }
  } = useContext(AppContext);

  const { allowedLanguages } = useContext(IntlWrapperContext);

  const [showLanguageSelectionModal, setShowLanguageSelectionModal] = useState<boolean>(false);
  const [showTermsOfServiceEditModal, setShowTermsOfServiceEditModal] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');

  if (textLoading) {
    return null;
  }

  //Skip the language selection modal if only a single language is allowed.
  const languageSelectionEnabled: boolean = allowedLanguages.length > 1;

  /**
   * Renders the language selection modal
   * @returns React Element
   */
  function renderLanguageSelectionModal(): React.ReactElement {
    return (
      <LanguageSelectionModal
        termsOfServiceType={termsOfServiceType}
        show={showLanguageSelectionModal}
        onHide={() => setShowLanguageSelectionModal(false)}
        onSubmit={language => {
          setSelectedLanguage(language);
          setShowTermsOfServiceEditModal(true);
          setShowLanguageSelectionModal(false);
        }}
      />
    );
  }

  /**
   * Renders the term of service edit modal
   * @returns React Element
   */
  function renderTermsOfServiceEditModal(): React.ReactElement {
    return (
      <TermsOfServiceEditModal
        show={showTermsOfServiceEditModal}
        onHide={() => setShowTermsOfServiceEditModal(false)}
        languageCode={selectedLanguage}
        termsOfServiceType={termsOfServiceType}
      />
    );
  }

  return (
    <>
      {canUpdateNode && (
        <Dropdown
          className={cx('ml-auto')}
          title={formatMessage('actionMenu.title')}
          aria-label={formatMessage('actionMenu.ariaLabel')}
        >
          <Dropdown.Toggle
            as={Button}
            id="lia-terms-of-service-action-menu"
            aria-label={formatMessage('toggleButtonLabel')}
            className={cx('lia-g-icon-btn')}
            variant={ButtonVariant.NO_VARIANT}
          >
            <Icon
              icon={Icons.GearIcon}
              size={IconSize.PX_20}
              testId="TermsOfServiceActionMenuItem.GearIcon"
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            align="right"
            aria-labelledby="lia-terms-of-service-action-menu"
            popperConfig={dropdownPopperConfig}
            renderOnMount
            data-testid="TermsOfServiceActionMenuItem.dropdown"
            className={cx('lia-dropdown-menu-index')}
          >
            <Dropdown.Item
              key={'edit'}
              className={cx('lia-dropdown-item')}
              data-testid="TermsOfServiceActionMenuItem.Edit"
              onClick={(): void => {
                if (languageSelectionEnabled) {
                  setShowLanguageSelectionModal(true);
                } else {
                  setSelectedLanguage(allowedLanguages[0]);
                  setShowTermsOfServiceEditModal(true);
                }
              }}
            >
              {formatMessage('edit')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      )}
      {showLanguageSelectionModal && renderLanguageSelectionModal()}
      {showTermsOfServiceEditModal && renderTermsOfServiceEditModal()}
    </>
  );
};

export default TermsOfServiceActionMenu;
